import type { ProtectionHealth } from '@ward/core';
import { fmtNum } from '../../ui/kit';
import { categoryLabel } from './common';

/**
 * Small fact rows (inside a <dl className="facts">) for protection data reported by browsers:
 * the protection bundle health on the device page and the threat verdict on an activity event.
 */

const RESULT: Record<string, string> = { activated: 'up to date (new version activated)', unchanged: 'up to date', rejected: 'rejected — kept the previous version', error: 'sync failed — kept the previous version', unsupported: 'server does not provide protection' };

export function ProtectionHealthFacts({ p }: { p: ProtectionHealth | null | undefined }) {
  if (!p) return <><dt>Threat protection</dt><dd className="sub">Not reported (extension version without protection bundle support)</dd></>;
  return (
    <>
      <dt>Threat protection</dt>
      <dd data-testid="device-protection">
        {p.version !== null ? `bundle v${p.version}` : 'no bundle active'}{p.lastResult && <span className="sub"> · {RESULT[p.lastResult] ?? p.lastResult}</span>}
        {p.lastError && <div className="sub">Last error: {p.lastError}</div>}
        <div className="sub">
          Web: {fmtNum(p.webInstalledDomains)} of {fmtNum(p.webDomains)} blocked domains active{p.webTruncatedDomains ? ` (${fmtNum(p.webTruncatedDomains)} left out: browser limit)` : ''}
          {p.trackerDomainsBlocked || p.stripParams ? ` · Privacy: ${fmtNum(p.trackerDomainsBlocked)} tracker domains, ${fmtNum(p.stripParams)} tracking parameters` : ''}
        </div>
      </dd>
      {(p.disabledByWard.length > 0 || p.couldNotDisable.length > 0) && (
        <>
          <dt>Blocked extensions</dt>
          <dd>
            {p.disabledByWard.length > 0 && <div>Disabled by Ward: {p.disabledByWard.map((id) => <code className="mr-8" key={id}>{id}</code>)}</div>}
            {p.couldNotDisable.length > 0 && <div className="text-block">Could not disable (installed by policy or protected): {p.couldNotDisable.map((id) => <code className="mr-8" key={id}>{id}</code>)}</div>}
          </dd>
        </>
      )}
    </>
  );
}

const SOURCE: Record<string, string> = { protection_list: 'protection list in the browser', reputation_lookup: 'server reputation lookup', download_policy: 'download rule (unfamiliar site / file type)' };

export function ThreatFacts({ t }: { t: { category: string | null; source: string; reason: string } | null | undefined }) {
  if (!t) return null;
  return (
    <>
      <dt>Threat</dt>
      <dd data-testid="event-threat">{t.category ? categoryLabel(t.category) : 'Uncategorised'} <span className="sub">· {SOURCE[t.source] ?? t.source} · {t.reason.replace(/_/g, ' ')}</span></dd>
    </>
  );
}
